import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api.ts";
import { toast } from "react-toastify";

const PatientSelector = ({ selectedPatients, onPatientsChange }) => {
    const navigate = useNavigate();
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchPatients = useCallback(async () => {
        try {
            setLoading(true);
            const response = await API.get('/data_manager/patients/');
            setPatients(response.data);
        } catch (err) {
            console.error("Error fetching patients:", err);
            toast.error("Failed to fetch patients.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPatients();
    }, [fetchPatients]);

    const handleTogglePatient = (patient) => {
        const isSelected = selectedPatients.some(p => p.id === patient.id);
        if (isSelected) {
            onPatientsChange(selectedPatients.filter(p => p.id !== patient.id));
        } else {
            onPatientsChange([...selectedPatients, patient]);
        }
    };

    const handleSelectAll = () => {
        onPatientsChange(filteredPatients);
    };

    const handleDeselectAll = () => {
        onPatientsChange([]);
    };

    const filteredPatients = patients.filter(patient => {
        const search = searchTerm.toLowerCase();
        return (
            patient.patient_id?.toLowerCase().includes(search) ||
            patient.name?.toLowerCase().includes(search) ||
            patient.notes?.toLowerCase().includes(search)
        );
    });

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleDateString();
    };

    const getGenderLabel = (gender) => {
        const labels = { M: "Male", F: "Female", O: "Other", U: "Unknown" };
        return labels[gender] || "-";
    };

    if (loading) {
        return (
            <div className="patient-selector-loading">
                <span className="spinner-border spinner-border-sm me-2"></span>
                Loading patients...
            </div>
        );
    }

    return (
        <div className="patient-selector">
            <div className="patient-selector-header">
                <div className="search-input-wrapper">
                    <i className="fa fa-search search-icon"></i>
                    <input
                        type="text"
                        className="form-control search-input"
                        placeholder="Search patients..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button
                            className="clear-search"
                            onClick={() => setSearchTerm("")}
                        >
                            <i className="fa fa-times"></i>
                        </button>
                    )}
                </div>
                <div className="patient-selector-actions">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-success"
                        onClick={() => navigate("/CreatePatient")}
                    >
                        <i className="fa fa-plus me-1"></i>
                        Create New Patient
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={handleSelectAll}
                    >
                        Select All
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleDeselectAll}
                    >
                        Deselect All
                    </button>
                </div>
            </div>

            <div className="patient-selector-info">
                <span className="text-muted">
                    {selectedPatients.length} of {patients.length} patients selected
                </span>
            </div>

            {patients.length === 0 ? (
                <div className="empty-state">
                    <i className="fa fa-users"></i>
                    <p>No patients found</p>
                    <small className="text-muted">
                        Create patients first using the Patient Manager
                    </small>
                    <button
                        type="button"
                        className="btn btn-success mt-3"
                        onClick={() => navigate("/CreatePatient")}
                    >
                        <i className="fa fa-plus me-2"></i>
                        Create New Patient
                    </button>
                </div>
            ) : (
                <div className="patient-selector-list">
                    {filteredPatients.map((patient) => {
                        const isSelected = selectedPatients.some(p => p.id === patient.id);
                        return (
                            <div
                                key={patient.id}
                                className={`patient-selector-item ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleTogglePatient(patient)}
                            >
                                <div className="patient-selector-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => {}}
                                    />
                                </div>
                                <div className="patient-selector-info-content">
                                    <div className="patient-selector-main">
                                        <strong>{patient.patient_id}</strong>
                                        {patient.name && (
                                            <span className="patient-name"> - {patient.name}</span>
                                        )}
                                    </div>
                                    <div className="patient-selector-details">
                                        {patient.gender && (
                                            <span className={`badge bg-${patient.gender === 'M' ? 'primary' : patient.gender === 'F' ? 'danger' : 'secondary'} me-2`}>
                                                {getGenderLabel(patient.gender)}
                                            </span>
                                        )}
                                        {patient.date_of_birth && (
                                            <span className="text-muted me-2">
                                                <i className="fa fa-calendar me-1"></i>
                                                {formatDate(patient.date_of_birth)}
                                            </span>
                                        )}
                                        {patient.data_count && (
                                            <span className="data-count-info">
                                                <i className="fa fa-database me-1"></i>
                                                {patient.data_count.total} data items
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Selected patients summary */}
            {selectedPatients.length > 0 && (
                <div className="patient-selector-summary">
                    <h6>Selected Patients:</h6>
                    <div className="selected-patients-tags">
                        {selectedPatients.map(patient => (
                            <span key={patient.id} className="patient-tag">
                                {patient.patient_id}
                                <button
                                    type="button"
                                    className="patient-tag-remove"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleTogglePatient(patient);
                                    }}
                                >
                                    <i className="fa fa-times"></i>
                                </button>
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PatientSelector;
